// The published @veecode-platform/backstage-plugin-gitlab-pipelines@0.7.0
// *does* ship `./alpha` (entity card + entity content). It also builds an
// ApiBlueprint in dist/alpha/apis.esm.js, but that factory is imported as a
// side effect and never added to createFrontendPlugin({ extensions }).
// NFS therefore discovers the UI without plugin.gitlabpipelines, and the
// entity tab crashes. Keep this local registration until upstream includes
// the blueprint in the plugin extensions array.
import type {
  DiscoveryApi,
  FetchApi,
  OAuthApi,
} from '@backstage/frontend-plugin-api';

import { GitlabAuthRequiredError } from './gitlabPipelinesAuth';

const GITLAB_PIPELINES_PROXY_URL = '/gitlab/api';

// GitLab OAuth tokens typically persist `read_user` and `api` only. Asking for
// `read_api`/`read_repository` as extra strings fails the in-memory scope check
// on remount and reopens the login dialog on every CI/CD tab visit.
const GITLAB_TOKEN_SCOPES = ['read_user', 'api'];

export type GitlabPipelinesClientOptions = {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  gitlabAuthApi: OAuthApi;
  proxyPath?: string;
};

class Client {
  private readonly discoveryApi: DiscoveryApi;
  private readonly proxyPath: string;
  private readonly fetchApi: FetchApi;
  private readonly gitlabAuthApi: OAuthApi;

  constructor(opts: GitlabPipelinesClientOptions) {
    this.discoveryApi = opts.discoveryApi;
    this.proxyPath = opts.proxyPath ?? GITLAB_PIPELINES_PROXY_URL;
    this.fetchApi = opts.fetchApi;
    this.gitlabAuthApi = opts.gitlabAuthApi;
  }

  private async fetch(input: string, gitlabReposlug: string, init?: RequestInit) {
    let token: string;
    try {
      token = await this.gitlabAuthApi.getAccessToken(GITLAB_TOKEN_SCOPES);
    } catch {
      throw new GitlabAuthRequiredError();
    }
    const apiUrl = await this.apiUrl(gitlabReposlug);
    const resp = await this.fetchApi.fetch(`${apiUrl}${input}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init?.headers as Record<string, string> | undefined),
      },
    });
    if (resp.status === 401) {
      throw new GitlabAuthRequiredError();
    }
    if (!resp.ok) {
      throw new Error(`Request failed - ${(await resp.json()).message}`);
    }
    return await resp.json();
  }

  private async apiUrl(gitlabReposlug: string) {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    return `${baseUrl}${this.proxyPath}/projects/${encodeURIComponent(
      gitlabReposlug,
    )}`;
  }

  async listBranchesFromRepo(gitlabReposlug: string) {
    return await this.fetch(`/repository/branches`, gitlabReposlug);
  }

  async listProjectPipelines(gitlabReposlug: string, branch: string) {
    const response = await this.fetch(
      `/pipelines?ref=${branch}`,
      gitlabReposlug,
    );
    return response;
  }

  async getSinglePipeline(
    gitlabReposlug: string,
    pipelineId: string,
    branch: string,
  ) {
    const response = await this.fetch(
      `/pipelines/${pipelineId}?ref=${branch}`,
      gitlabReposlug,
    );
    return response;
  }

  async getLatestPipeline(gitlabReposlug: string, branch: string) {
    const response = await this.fetch(
      `/pipelines/latest?ref=${branch}`,
      gitlabReposlug,
    );
    return response;
  }

  async runNewPipeline(
    gitlabReposlug: string,
    branch: string,
    variables: unknown,
  ) {
    const requestBody = {
      ref: branch,
      variables,
    };
    const response = await this.fetch(`/pipeline?ref=${branch}`, gitlabReposlug, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    return response;
  }

  async runNewPipelineWithTrigger(
    gitlabReposlug: string,
    triggerToken: string,
    branch: string,
  ) {
    const requestBody = {
      token: triggerToken,
      ref: branch,
    };
    const response = await this.fetch(`/trigger/pipeline`, gitlabReposlug, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    return response;
  }

  async retryPipelineJobs(gitlabReposlug: string, pipelineId: string) {
    const response = await this.fetch(
      `/pipelines/${pipelineId}/retry`,
      gitlabReposlug,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return response;
  }

  async cancelPipelineJobs(
    gitlabReposlug: string,
    pipelineId: string,
    branch: string,
  ) {
    const response = await this.fetch(
      `/pipelines/${pipelineId}/cancel?ref=${branch}`,
      gitlabReposlug,
      {
        method: 'POST',
      },
    );
    return response;
  }

  async listPipelineJobs(
    gitlabReposlug: string,
    pipelineId: string,
    branch: string,
  ) {
    const response = await this.fetch(
      `/pipelines/${pipelineId}/jobs?ref=${branch}`,
      gitlabReposlug,
    );
    return response;
  }

  async getSingleJob(gitlabReposlug: string, jobId: string, branch: string) {
    const response = await this.fetch(`/jobs/${jobId}?ref=${branch}`, gitlabReposlug);
    return response;
  }

  async runJob(
    gitlabReposlug: string,
    jobId: string,
    params: unknown,
    branch: string,
  ) {
    const requestBody = {
      job_variables_attributes: params,
    };
    const response = await this.fetch(
      `/jobs/${jobId}/play?ref=${branch}`,
      gitlabReposlug,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      },
    );
    return response;
  }

  async cancelJob(gitlabReposlug: string, jobId: string, branch: string) {
    const response = await this.fetch(
      `/jobs/${jobId}/cancel?ref=${branch}`,
      gitlabReposlug,
      {
        method: 'POST',
      },
    );
    return response;
  }

  async retryJob(gitlabReposlug: string, jobId: string, branch: string) {
    const response = await this.fetch(
      `/jobs/${jobId}/retry?ref=${branch}`,
      gitlabReposlug,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return response;
  }
}

export class GitlabPipelinesApiClient {
  private readonly client: Client;

  constructor(opts: GitlabPipelinesClientOptions) {
    this.client = new Client(opts);
  }

  async listBranchesFromRepo(gitlabReposlug: string) {
    return this.client.listBranchesFromRepo(gitlabReposlug);
  }

  async listProjectPipelines(gitlabReposlug: string, branch: string) {
    return this.client.listProjectPipelines(gitlabReposlug, branch);
  }

  async getSinglePipeline(
    gitlabReposlug: string,
    pipelineId: string,
    branch: string,
  ) {
    return this.client.getSinglePipeline(gitlabReposlug, pipelineId, branch);
  }

  async getLatestPipeline(gitlabReposlug: string, branch: string) {
    return this.client.getLatestPipeline(gitlabReposlug, branch);
  }

  async runNewPipeline(
    gitlabReposlug: string,
    branch: string,
    variables: unknown,
  ) {
    return this.client.runNewPipeline(gitlabReposlug, branch, variables);
  }

  async runNewPipelineWithTrigger(
    gitlabReposlug: string,
    triggerToken: string,
    branch: string,
  ) {
    return this.client.runNewPipelineWithTrigger(
      gitlabReposlug,
      triggerToken,
      branch,
    );
  }

  async retryPipelineJobs(gitlabReposlug: string, pipelineId: string) {
    return this.client.retryPipelineJobs(gitlabReposlug, pipelineId);
  }

  async cancelPipelineJobs(
    gitlabReposlug: string,
    pipelineId: string,
    branch: string,
  ) {
    return this.client.cancelPipelineJobs(gitlabReposlug, pipelineId, branch);
  }

  async listPipelineJobs(
    gitlabReposlug: string,
    pipelineId: string,
    branch: string,
  ) {
    return this.client.listPipelineJobs(gitlabReposlug, pipelineId, branch);
  }

  async getSingleJob(gitlabReposlug: string, jobId: string, branch: string) {
    return this.client.getSingleJob(gitlabReposlug, jobId, branch);
  }

  async runJob(
    gitlabReposlug: string,
    jobId: string,
    params: unknown,
    branch: string,
  ) {
    return this.client.runJob(gitlabReposlug, jobId, params, branch);
  }

  async cancelJob(gitlabReposlug: string, jobId: string, branch: string) {
    return this.client.cancelJob(gitlabReposlug, jobId, branch);
  }

  async retryJob(gitlabReposlug: string, jobId: string, branch: string) {
    return this.client.retryJob(gitlabReposlug, jobId, branch);
  }
}
